import {
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
} from "posthog-js/react";
import { getPartnerSlug } from "@utils";
import { getEnvironment } from "@utils/getEnvironment";
import {
  ABTestData,
  FeatureFlag,
  FeatureFlagData,
  FeatureFlagStatus,
} from "src/types";

// Type guard function to validate that the payload matches the FeatureFlag type
export const isFeatureFlag = (data: any): data is FeatureFlag => {
  return (
    data &&
    ["development", "qa", "stage", "production"].every((env) => {
      const envData = data[env];
      return (
        envData &&
        typeof envData.active === "boolean" &&
        Array.isArray(envData.include) &&
        Array.isArray(envData.exclude)
      );
    })
  );
};

export const checkEnvStatus = (flagIdentifier: string): FeatureFlagStatus => {
  const domainData = getPartnerSlug(getEnvironment()); // Get partner from domain data
  const data: any = useFeatureFlagPayload(flagIdentifier); // Retrieve the payload

  // Validate that the data matches the expected FeatureFlag type
  if (!isFeatureFlag(data)) {
    return {
      active: false,
    }; // If validation fails, return false
  }

  // Now that data is of type FeatureFlag, you can safely use it
  const envData: FeatureFlagData = data[domainData.environment];

  // Check if the feature flag is active for the environment
  if (!envData.active) {
    return {
      active: false,
    };
  }

  // Check if partner is included or excluded
  const { include, exclude, extra } = envData;

  // Include check: if "*" is in include array or domainData.partner is explicitly included
  const isIncluded =
    include.includes("*") || include.includes(domainData.partner);

  // Exclude check: if domainData.partner is in exclude array
  const isExcluded = exclude.includes(domainData.partner);

  // If both include and exclude checks are true, the feature is active
  const active = isIncluded && !isExcluded ? true : false;

  // Return true only if included and not excluded, otherwise false
  return {
    active,
    ...(extra && {
      extra,
    }),
  };
};

export const checkAbTestStatus = (flagIdentifier: string): ABTestData => {
  const checkStatus = checkEnvStatus(flagIdentifier);
  const variant: any = useFeatureFlagVariantKey(flagIdentifier); // Retrieve the variant

  return {
    isTestVariant: variant === "test",
    ...checkStatus,
  };
};

export const featureFlags = {
  IsKnabPrefillDeeplinkEnabled: () => {
    //This is no longer used as a feature flag
    const host: string = window.location.host;
    const [partner] = host.split(".");

    return partner === "knab" ? true : false;
  },
  IsAiAgentProfessionSearcher: () =>
    checkEnvStatus("tech-5314-AI-agent-profession-searcher"),
  IsAiAgentSupport: () => checkEnvStatus("TECH-5425-AI-agent-support"),
  IsTickerEnabled: () => checkEnvStatus("tech-4596-usp-ticker"),
};
